import React, { useMemo, useState } from 'react';
import { Card, CardContent, Grid, Typography, Box, Chip, IconButton, Tooltip, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { RolesEnum } from 'features/common/types';
import { IFlagShowCardInfo, ITransactionLineToDetail } from 'features/transactions/types';
import {
	IAuthorizationWithCrosses,
	ISaleOrderSummary,
	ITransactionFinalization,
} from 'features/transaction-details/types';
import { getAuthorizationRefundImpacts } from 'features/transaction-details/helpers/transactionRefundImpact';
import { canShowCardSensitiveInfo } from 'features/transaction-details/mappers';
import { CreditCardDisplay } from '../CreditCardDisplay';
import { TransactionAmountSummary } from '../TransactionAmountSummary';
import { TransactionAssociatedSaleOrder } from '../TransactionAssociatedSaleOrder';
import { copyToClipboard } from 'features/transaction-details/helpers';
import { getCardInfo } from 'features/transactions/services';

interface TransactionHeaderProps {
	transaction: ITransactionLineToDetail;
	saleOrder: ISaleOrderSummary | null;
	flagShowCardInfo: IFlagShowCardInfo;
	finalizations?: ITransactionFinalization[];
	authorizations?: IAuthorizationWithCrosses[];
	onCopySuccess: (message: string) => void;
}

export const TransactionHeader = ({
	transaction,
	saleOrder,
	flagShowCardInfo,
	finalizations = [],
	authorizations = [],
	onCopySuccess,
}: TransactionHeaderProps): JSX.Element => {
	const refundImpacts = useMemo(() => getAuthorizationRefundImpacts(authorizations), [authorizations]);
	const { auth } = useSelector((state: RootState) => state);
	const [showSecurityCode, setShowSecurityCode] = useState(false);
	const [cardFullInfo, setCardFullInfo] = useState<{ cardNumber: number; cardSecurityCode: number } | null>(null);
	const [loadingSecurityCode, setLoadingSecurityCode] = useState(false);

	const isAdminOrSuperAdminOrOperator =
		auth.user?.role === RolesEnum.SUPERADMIN ||
		auth.user?.role === RolesEnum.ADMIN ||
		auth.user?.role === RolesEnum.OPERATOR;

	const handleCopy = async (text: string, label: string): Promise<void> => {
		const success = await copyToClipboard(text);
		if (success) {
			onCopySuccess(`${label} copiado`);
		}
	};

	const handleToggleSecurityCode = async (): Promise<void> => {
		if (!showSecurityCode && !cardFullInfo) {
			setLoadingSecurityCode(true);
			try {
				const response = await getCardInfo(transaction.id);
				setCardFullInfo({
					cardNumber: response.data.cardNumber,
					cardSecurityCode: response.data.cardSecurityCode,
				});
				setShowSecurityCode(true);
			} catch (error) {
				console.error('Error al obtener información de tarjeta:', error);
				onCopySuccess('Error al obtener información de tarjeta');
			} finally {
				setLoadingSecurityCode(false);
			}
		} else {
			setShowSecurityCode(!showSecurityCode);
		}
	};

	const canRevealCardInfo = isAdminOrSuperAdminOrOperator && canShowCardSensitiveInfo(flagShowCardInfo);
	const showFullCardNumber = showSecurityCode && cardFullInfo && flagShowCardInfo.showCreditCardNumber;
	const showFullSecurityCode = showSecurityCode && cardFullInfo && flagShowCardInfo.showSecurityCode;
	const displayCardNumber =
		showFullCardNumber && cardFullInfo ? cardFullInfo.cardNumber.toString() : transaction.cardNumberFormatted;
	const displaySecurityCode =
		showFullSecurityCode && cardFullInfo
			? cardFullInfo.cardSecurityCode.toString()
			: transaction.cardSecurityCodeFormatted;

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Typography variant="h5" color="primary">
							Transacción #{transaction.id}
						</Typography>
						<Tooltip title="Copiar ID de transacción">
							<IconButton size="small" onClick={() => handleCopy(transaction.id.toString(), 'ID de transacción')}>
								<ContentCopyIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>

				<Grid container spacing={3}>
					{/* Tarjeta de crédito */}
					<Grid item xs={12} md={4}>
						<CreditCardDisplay
							cardNumber={displayCardNumber}
							cardholder={transaction.cardholder}
							expiration={transaction.cardExpiration}
							securityCode={displaySecurityCode}
							cardStamp={transaction.cardStamp}
							onCopyNumber={() => handleCopy(displayCardNumber, 'Número de tarjeta')}
							onCopyHolder={() => handleCopy(transaction.cardholder, 'Titular')}
							onCopyExpiration={() => handleCopy(transaction.cardExpiration, 'Vencimiento')}
							onCopySecurityCode={() => handleCopy(displaySecurityCode, 'CVV')}
						/>

						{/* Cuotas y botón de ver info en una sola fila compacta */}
						<Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography variant="caption" color="text.secondary">
									Cuotas:
								</Typography>
								<Chip label={transaction.fee} size="small" color="primary" />
							</Box>

							{canRevealCardInfo && (
								<Button
									size="small"
									variant="text"
									startIcon={showSecurityCode ? <VisibilityOffIcon /> : <VisibilityIcon />}
									onClick={handleToggleSecurityCode}
									disabled={loadingSecurityCode}
									sx={{ minWidth: 'auto', ml: 'auto' }}
								>
									{loadingSecurityCode ? 'Cargando...' : showSecurityCode ? 'Ocultar' : 'Ver Info'}
								</Button>
							)}
						</Box>

						<TransactionAssociatedSaleOrder
							saleOrder={saleOrder}
							crmTicket={transaction.crmTicket}
							refundImpacts={refundImpacts}
							coinCode={transaction.coinCode}
							onCopy={handleCopy}
							onNavigationError={(message) => onCopySuccess(message)}
						/>
					</Grid>

					{/* Resumen de montos */}
					<Grid item xs={12} md={8}>
						<TransactionAmountSummary
							transaction={transaction}
							finalizations={finalizations}
							authorizations={authorizations}
							dealId={transaction.crmTicket}
							saleOrderId={saleOrder?.id}
							onNavigationError={(message) => onCopySuccess(message)}
						/>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};
